import { Icon } from "@chakra-ui/react";
import { t } from "i18next";
import { FaBalanceScale, FaCalculator, FaMoneyBill, FaRobot } from "react-icons/fa";
import { ReceivedBseBidDto } from "../../../../../autogen/bff-api";
import { ProductGroup } from "../../../../../common/types";

// Enum for model types
export enum EvaluationModelType {
  LOWEST_PRICE = "lowest_price",
  PRICE_QUALITY = "price_quality",
  QUALITY_ADJUSTED = "quality_adjusted",
  QUALITATIVE_AI = "qualitative_ai",
}

export interface EvaluationModelContext {
  bids: ReceivedBseBidDto[];
  productGroups: ProductGroup[];
  selectedBids?: unknown[];
  sourcingEventId?: string;
}
export interface ScoredBid {
  bidId: string;
  supplierId: string;
  supplierName: string;
  totalScore: number;
  priceScore?: number;
  qualityScore?: number;
  details?: Record<string, unknown>;
  isWinner?: boolean;
}

export interface EvaluationResult {
  scoredBids: ScoredBid[];
  bestBidId?: string;
  bestScore?: number;
  selectedBidIds?: string[];
  aiEvaluation?: {
    response: string | null;
    isLoading: boolean;
    instructions?: string;
  };
}

// Extended winning bids with the winning bids property
export interface ExtendedWinningBids {
  productGroupId: string;
  winningBids: string[];
}

export interface EvaluationModel {
  type: EvaluationModelType;
  name(): JSX.Element;
  description(): JSX.Element;
  getModelIcon(): JSX.Element;
}

// Props for model configuration components
export interface ModelConfigProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

export const evaluationModels: Record<string, EvaluationModel> = {
  price: {
    type: EvaluationModelType.LOWEST_PRICE,
    name: () => <>{t("Lowest Price")}</>,
    description: () => <>{t("Evaluate bids based solely on price")}</>,
    getModelIcon: () => <Icon as={FaMoneyBill} />,
  },
  priceQuality: {
    type: EvaluationModelType.PRICE_QUALITY,
    name: () => <>{t("Weighted Price-Quality")}</>,
    description: () => <>{t("Traditional model with configurable weights for price and quality factors")}</>,
    getModelIcon: () => <Icon as={FaBalanceScale} />,
  },
  qualityAdjusted: {
    type: EvaluationModelType.QUALITY_ADJUSTED,
    name: () => <>{t("Quality Adjusted Price")}</>,
    description: () => (
      <>
        {t(
          "Price adjustment model where quality benefits are converted to monetary value and subtracted from bid price"
        )}
      </>
    ),
    getModelIcon: () => <Icon as={FaCalculator} />,
  },
  qualitativeAi: {
    type: EvaluationModelType.QUALITATIVE_AI,
    name: () => <>{t("AI Evaluation")}</>,
    description: () => <>{t("Use AI to analyze and score all aspects of bids")}</>,
    getModelIcon: () => <Icon as={FaRobot} />,
  },
};
